import React, { useEffect, useState } from 'react';

interface OccasionalScreenGlitchProps {
  enabled?: boolean;
}

export const OccasionalScreenGlitch: React.FC<OccasionalScreenGlitchProps> = ({ enabled = true }) => {
  const [isGlitching, setIsGlitching] = useState(false);
  const [glitchIntensity, setGlitchIntensity] = useState<'minor' | 'major'>('minor');

  useEffect(() => {
    if (!enabled) return;

    let timeoutId: NodeJS.Timeout;

    const scheduleNextGlitch = () => {
      // Random delay between 12s and 35s
      const delay = Math.floor(Math.random() * 23000) + 12000;

      timeoutId = setTimeout(() => {
        // 20% chance for major glitch, 80% for minor
        const isMajor = Math.random() < 0.2;
        setGlitchIntensity(isMajor ? 'major' : 'minor');
        setIsGlitching(true);

        const duration = isMajor ? 320 : 180;

        setTimeout(() => {
          setIsGlitching(false);
          scheduleNextGlitch();
        }, duration);
      }, delay);
    };

    scheduleNextGlitch();

    return () => clearTimeout(timeoutId);
  }, [enabled]);

  if (!enabled || !isGlitching) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[99999] overflow-hidden">
      {/* Cyan RGB Shift Layer */}
      <div 
        className="absolute inset-0 bg-cyan-500/10 mix-blend-screen animate-glitch-chromatic-cyan"
        style={{ clipPath: glitchIntensity === 'major' ? 'polygon(0 15%, 100% 15%, 100% 35%, 0 35%, 0 65%, 100% 65%, 100% 85%, 0 85%)' : 'polygon(0 30%, 100% 30%, 100% 45%, 0 45%)' }}
      />
      
      {/* Cyberpunk Yellow RGB Shift Layer */}
      <div 
        className="absolute inset-0 bg-[#FCEE0A]/12 mix-blend-screen animate-glitch-chromatic-yellow"
        style={{ clipPath: glitchIntensity === 'major' ? 'polygon(0 35%, 100% 35%, 100% 55%, 0 55%, 0 75%, 100% 75%, 100% 95%, 0 95%)' : 'polygon(0 10%, 100% 10%, 100% 25%, 0 25%)' }}
      />

      {/* Rose/Magenta RGB Shift Layer */}
      <div 
        className="absolute inset-0 bg-rose-500/10 mix-blend-screen animate-glitch-chromatic-rose"
        style={{ clipPath: glitchIntensity === 'major' ? 'polygon(0 5%, 100% 5%, 100% 25%, 0 25%, 0 75%, 100% 75%, 100% 90%, 0 90%)' : 'polygon(0 60%, 100% 60%, 100% 75%, 0 75%)' }}
      />

      {/* Horizontal High-Speed Scan Line */}
      <div className="absolute left-0 right-0 h-2 bg-[#FCEE0A]/40 blur-[1px] animate-glitch-scan-line" />
    </div>
  );
};
